import { Button } from '@/components/ui/button';
import { Typo } from '@/components/ui/typography';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { AppliedVoucher } from '@/types/voucher';
import { router } from '@inertiajs/react';
import { Loader2, Tag, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

interface SummaryVoucherProps {
  voucher: AppliedVoucher;
  /** The rows being paid for. Narrows the matched count to the current selection. */
  selectedItems?: UserCartItem[];
}

export function SummaryVoucher({ voucher, selectedItems }: SummaryVoucherProps) {
  const { removeVoucher, isFetching, matchedRowIds } = useUserGlobalCart();
  const [isApplying, setIsApplying] = useState(false);

  // The hook's matchedRowIds is in-memory only and empties on every page load,
  // so prefer the session payload and intersect it with what is selected.
  const matchedCount = useMemo(() => {
    const sessionRowIds = voucher.matched_row_ids ?? [];
    if (sessionRowIds.length === 0) return matchedRowIds.length;
    if (!selectedItems) return sessionRowIds.length;

    const selectedRowIds = new Set(selectedItems.map((item) => item.rowId));
    return sessionRowIds.filter((rowId) => selectedRowIds.has(rowId)).length;
  }, [voucher.matched_row_ids, matchedRowIds, selectedItems]);

  const handleRemoveVoucher = async () => {
    setIsApplying(true);
    try {
      const response = await removeVoucher();

      if (response?.success) {
        toast.success(response.message || 'Đã xóa voucher');
        router.reload({ only: ['appliedVoucher'] });
      } else {
        toast.error(response?.message || 'Không thể xóa voucher');
      }
    } catch (error: any) {
      console.error('Error removing voucher:', error);
      toast.error(error?.message || 'Đã có lỗi xảy ra');
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-green-200 bg-green-50 p-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2">
            <Tag className="mt-0.5 h-4 w-4 text-green-600" />
            <div className="flex-1">
              <Typo variant="small" className="font-medium text-green-900">
                Voucher đã áp dụng
              </Typo>
              <code className="mt-1 block font-mono text-sm font-bold text-green-700">{voucher.code}</code>
              {voucher.discount_label && (
                <Typo variant="muted" className="mt-1 text-xs text-green-600">
                  Giảm {voucher.discount_label}
                </Typo>
              )}
              {matchedCount > 0 && (
                <Typo variant="muted" className="mt-0.5 text-xs text-green-600">
                  Áp dụng cho {matchedCount} sản phẩm
                </Typo>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemoveVoucher}
            disabled={isApplying || isFetching}
            className="h-6 w-6 p-0 text-green-600 hover:bg-green-100 hover:text-green-700"
          >
            {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
